import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  Link,
} from '@mui/material';
import {
  CalendarToday,
  Schedule,
  Repeat,
  Edit,
  Pause,
  Refresh,
  TrendingUp,
  SmartToy,
  Lightbulb,
  Publish,
  DeleteOutline,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { addDays } from 'date-fns';
import { schedulingAPI, postsAPI, platformAccountsAPI } from '../services/api';
import { formatInTimeZone } from 'date-fns-tz';
import { useToast } from '../contexts/ToastContext';
import { useClients } from '../contexts/ClientContext';
import MlEstimateBadge from '../components/MlEstimateBadge';
import PrePublishScorePanel from '../components/PrePublishScorePanel';
import { SHOW_INSTAGRAM_AS_PUBLISH_TARGET } from '../config/featureFlags';
import {
  connectedPlatformOptions,
  normalizePublishPlatformSlug,
} from '../utils/connectedPlatforms';
import Checkbox from '@mui/material/Checkbox';
import FormHelperText from '@mui/material/FormHelperText';
import ListItemText from '@mui/material/ListItemText';
import ScheduledPostsQueue from '../components/ScheduledPostsQueue';
import PostCalendar from '../components/PostCalendar';
import { parseApiUtcDate, formatSchedulingTime } from '../utils/datetime';
import ConfirmDialog from '../components/ConfirmDialog';
import PageLoadingState from '../components/PageLoadingState';
import PageErrorAlert from '../components/PageErrorAlert';
import { MetricCard, PageHeader } from '../components/primitives';
import { usePublishStatusPolling } from '../hooks/usePublishStatusPolling';

const SCHEDULING_TAB_KEYS = {
  calendar: 0,
  bulk: 1,
  recurring: 2,
  upcoming: 3,
  ai: 4,
  queue: 5,
};

/** Phase 0.2: all ML / heuristic slot math uses this zone (explicit, not browser-inferred). */
const PRIMARY_SCHEDULING_TZ = 'America/New_York';

const SUGGESTION_SOURCE_LABELS = {
  top_performers: 'Top-performing posts (historical)',
  peak_heuristic: 'Peak-hour fallback',
  sparse_fallback: 'Sparse data — near-future slot',
};

const AI_REC_SOURCE_LABELS = {
  industry_heuristic_constant: 'Industry defaults (not learned)',
};

// Bulk Scheduling Component — W3.BUG-3: reuse PrePublishScorePanel (same
// score-draft + services.cta_detection path as Gallery/Posts), not a local CTA check.
// W4.F2: per-item + bulk delete with confirm to clear stale unused drafts from the queue.
const BulkScheduling = ({ posts, onSchedule, platformAccounts, onRefresh }) => {
  const { showError, showSuccess } = useToast();
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [scheduledTime, setScheduledTime] = useState(new Date());
  const [timezone, setTimezone] = useState(PRIMARY_SCHEDULING_TZ);
  const [slotSuggestion, setSlotSuggestion] = useState(null);
  /** postId → target platform slugs (editable before schedule). */
  const [targetsByPostId, setTargetsByPostId] = useState({});
  const [savingTargets, setSavingTargets] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    ids: [],
  });
  const [deleteBusy, setDeleteBusy] = useState(false);

  const sortedPosts = useMemo(() => {
    const arr = Array.isArray(posts) ? [...posts] : [];
    arr.sort((a, b) => {
      const ad = a.status === 'draft' ? 0 : 1;
      const bd = b.status === 'draft' ? 0 : 1;
      if (ad !== bd) return ad - bd;
      return (b.id || 0) - (a.id || 0);
    });
    return arr;
  }, [posts]);

  const selectedPostObjs = useMemo(
    () => sortedPosts.filter((p) => p.id && selectedPosts.includes(p.id)),
    [sortedPosts, selectedPosts],
  );

  const firstSelectedPost = sortedPosts.find(
    (p) => p.id && selectedPosts.includes(p.id),
  );

  // W4.BUG-4: stable key so posts list poll/re-render does not re-apply ML
  // suggestion and wipe a user-edited DateTimePicker value.
  const suggestionContextKey = useMemo(() => {
    const first = sortedPosts.find((p) => p.id && selectedPosts.includes(p.id));
    if (!first?.client_id || first.status !== 'draft') return null;
    return [
      first.id,
      first.client_id,
      normalizePublishPlatformSlug(first.platform) || 'facebook',
      first.content_type || '',
    ].join('|');
  }, [sortedPosts, selectedPosts]);

  const plannedScheduleCount = useMemo(() => {
    return selectedPostObjs.reduce((n, post) => {
      const targets = (targetsByPostId[post.id] || []).filter(Boolean);
      return n + Math.max(1, targets.length);
    }, 0);
  }, [selectedPostObjs, targetsByPostId]);

  useEffect(() => {
    setTargetsByPostId((prev) => {
      const next = { ...prev };
      for (const post of selectedPostObjs) {
        if (next[post.id]) continue;
        const opts = connectedPlatformOptions(
          platformAccounts,
          post.client_id,
        ).map((o) => o.value);
        // Default to this row's publish platform only. Gallery multi-promote
        // already created sibling drafts per network; seeding the full
        // target_platforms list made Bulk Schedule look like 3-in-1.
        const primary = post.platform
          ? [normalizePublishPlatformSlug(post.platform)]
          : [];
        const fromStored =
          Array.isArray(post.target_platforms) && post.target_platforms.length
            ? post.target_platforms.map(normalizePublishPlatformSlug)
            : [];
        const preferred =
          primary.length === 1 && opts.includes(primary[0])
            ? primary
            : fromStored.filter((p) => opts.includes(p));
        next[post.id] = preferred.length
          ? preferred
          : opts.length
            ? [opts[0]]
            : [];
      }
      return next;
    });
  }, [selectedPostObjs, platformAccounts]);

  const fetchSuggestedSlot = useCallback(
    async ({ applyTime = false } = {}) => {
      const first = sortedPosts.find(
        (p) => p.id && selectedPosts.includes(p.id),
      );
      if (!first?.client_id || first.status !== 'draft') {
        setSlotSuggestion(null);
        return;
      }
      try {
        const r = await schedulingAPI.getSuggestedPostingSlot({
          client_id: first.client_id,
          platform: first.platform || 'facebook',
          content_type: first.content_type || undefined,
        });
        if (!r?.suggested_time) {
          setSlotSuggestion(null);
          return;
        }
        const d = new Date(r.suggested_time);
        // Only stamp the picker when selection context changes or user clicks Refresh.
        if (applyTime && !Number.isNaN(d.getTime())) setScheduledTime(d);
        setSlotSuggestion({
          source: r.source,
          scheduling_timezone: r.scheduling_timezone,
          suggested_time_display_et: r.suggested_time_display_et,
          source_detail: r.source_detail,
          suggested_time: r.suggested_time,
        });
      } catch {
        setSlotSuggestion(null);
      }
    },
    [selectedPosts, sortedPosts],
  );

  // Prefill once per draft selection — not on every posts poll (W4.BUG-4).
  useEffect(() => {
    if (!suggestionContextKey) {
      setSlotSuggestion(null);
      return;
    }
    fetchSuggestedSlot({ applyTime: true });
    // Intentionally omit fetchSuggestedSlot: identity churns when `posts` refreshes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestionContextKey]);

  const handleSchedule = async () => {
    if (selectedPosts.length === 0) return;
    setSavingTargets(true);
    try {
      // Phase 16.R8: product model is one Post row per publish target. Bulk used to
      // only stamp target_platforms on the existing row — Celery publishes
      // post.platform only, so extra networks never shipped. Fan out like Posts.js.
      const scheduleIds = [];
      const accounts = Array.isArray(platformAccounts) ? platformAccounts : [];
      const findAccount = (clientId, slug) =>
        accounts.find(
          (a) =>
            String(a.client_id) === String(clientId) &&
            normalizePublishPlatformSlug(a.platform) === slug &&
            a.is_active !== false,
        );

      for (const post of selectedPostObjs) {
        const targets = (targetsByPostId[post.id] || [])
          .map(normalizePublishPlatformSlug)
          .filter(Boolean);
        if (!targets.length) {
          showError(
            `Select at least one platform for “${post.title || post.id}”`,
          );
          return;
        }
        const primary = targets[0];
        const paPrimary = findAccount(post.client_id, primary);
        await postsAPI.updatePost(post.id, {
          platform: primary,
          target_platforms: targets,
          ...(paPrimary ? { platform_account_id: paPrimary.id } : {}),
        });
        scheduleIds.push(post.id);

        for (const extra of targets.slice(1)) {
          const pa = findAccount(post.client_id, extra);
          const payload = {
            client_id: post.client_id,
            content: post.content || '',
            content_type: post.content_type || 'text',
            status: 'draft',
            platform: extra,
            target_platforms: targets,
            ...(pa ? { platform_account_id: pa.id } : {}),
          };
          if (post.title) payload.title = post.title;
          if (Array.isArray(post.media_urls) && post.media_urls.length) {
            payload.media_urls = post.media_urls;
          } else if (
            typeof post.media_urls === 'string' &&
            post.media_urls.trim()
          ) {
            payload.media_urls = post.media_urls;
          }
          if (Array.isArray(post.hashtags) && post.hashtags.length) {
            payload.hashtags = post.hashtags;
          } else if (
            typeof post.hashtags === 'string' &&
            post.hashtags.trim()
          ) {
            payload.hashtags = post.hashtags;
          }
          if (
            post.platform_specific &&
            typeof post.platform_specific === 'object'
          ) {
            payload.platform_specific = post.platform_specific;
          }
          const created = await postsAPI.createPost(payload);
          if (created?.id != null) scheduleIds.push(created.id);
        }
      }
      await onSchedule(scheduleIds, scheduledTime, timezone);
      setSelectedPosts([]);
    } catch (error) {
      console.error('Error scheduling posts:', error);
      showError(
        error?.response?.data?.detail ||
          error?.message ||
          'Failed to update platforms / schedule',
      );
    } finally {
      setSavingTargets(false);
    }
  };

  const requestDeletePosts = (ids) => {
    const unique = [...new Set((ids || []).filter((id) => id != null))];
    if (!unique.length) return;
    setDeleteConfirm({ open: true, ids: unique });
  };

  const handleConfirmDeleteFromBulk = async () => {
    const ids = deleteConfirm.ids || [];
    if (!ids.length) return;
    setDeleteBusy(true);
    try {
      for (const id of ids) {
        await postsAPI.deletePost(id);
      }
      showSuccess(
        ids.length === 1
          ? 'Removed from bulk queue'
          : `Removed ${ids.length} posts from bulk queue`,
      );
      setSelectedPosts((prev) => prev.filter((id) => !ids.includes(id)));
      setDeleteConfirm({ open: false, ids: [] });
      if (typeof onRefresh === 'function') await onRefresh();
    } catch (err) {
      showError(
        err?.response?.data?.detail ||
          err?.message ||
          'Could not remove post(s)',
      );
    } finally {
      setDeleteBusy(false);
    }
  };

  const draftIds = useMemo(
    () =>
      sortedPosts
        .filter((p) => p.id && String(p.status || '').toLowerCase() === 'draft')
        .map((p) => p.id),
    [sortedPosts],
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Bulk Schedule Posts
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
              mb: 1,
            }}
          >
            <Typography variant="subtitle2">Select Posts</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              <Button
                size="small"
                variant="outlined"
                disabled={!draftIds.length}
                onClick={() => setSelectedPosts(draftIds)}
              >
                Select drafts
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={!selectedPosts.length}
                onClick={() => setSelectedPosts([])}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteOutline />}
                disabled={!selectedPosts.length || deleteBusy}
                onClick={() => requestDeletePosts(selectedPosts)}
              >
                Delete selected ({selectedPosts.length})
              </Button>
            </Box>
          </Box>
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {sortedPosts.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No posts in the bulk queue.
              </Typography>
            )}
            {sortedPosts.map((post) => (
              <Box
                key={post.id}
                sx={{
                  p: 1,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  mb: 1,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  bgcolor: selectedPosts.includes(post.id)
                    ? '#e3f2fd'
                    : 'white',
                }}
                onClick={() => {
                  if (selectedPosts.includes(post.id)) {
                    setSelectedPosts(
                      selectedPosts.filter((id) => id !== post.id),
                    );
                  } else {
                    setSelectedPosts([...selectedPosts, post.id]);
                  }
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {post.status === 'draft' ? '[Draft] ' : ''}
                    {post.title || 'Untitled Post'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {post.platform || '—'} •{' '}
                    {(post.content || '').slice(0, 50)}
                    {(post.content || '').length > 50 ? '…' : ''}
                  </Typography>
                </Box>
                <Tooltip title="Remove from queue">
                  <IconButton
                    size="small"
                    aria-label="Remove from bulk queue"
                    color="error"
                    disabled={deleteBusy}
                    onClick={(e) => {
                      e.stopPropagation();
                      requestDeletePosts([post.id]);
                    }}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Schedule Settings
          </Typography>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="Scheduled Time"
              value={scheduledTime}
              onChange={(date) => setScheduledTime(date)}
              renderInput={(params) => (
                <TextField {...params} fullWidth sx={{ mb: 2 }} />
              )}
            />
          </LocalizationProvider>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Refresh />}
              disabled={
                !firstSelectedPost || firstSelectedPost.status !== 'draft'
              }
              onClick={() => fetchSuggestedSlot({ applyTime: true })}
            >
              Refresh ML suggestion
            </Button>
            <Typography variant="caption" color="text.secondary">
              Suggested day/hour windows use Eastern Time (America/New_York), not your
              browser timezone.
            </Typography>
          </Box>

          {slotSuggestion && firstSelectedPost?.status === 'draft' && (
            <Alert severity="info" icon={<TrendingUp />} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {SUGGESTION_SOURCE_LABELS[slotSuggestion.source] ||
                  slotSuggestion.source}
              </Typography>
              <Typography variant="body2">
                {slotSuggestion.suggested_time_display_et || '—'} (
                {slotSuggestion.scheduling_timezone || PRIMARY_SCHEDULING_TZ})
              </Typography>
              {slotSuggestion.source_detail && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 0.5 }}
                >
                  {slotSuggestion.source_detail}
                </Typography>
              )}
            </Alert>
          )}

          {firstSelectedPost && (
            <Box sx={{ mb: 2 }}>
              <PrePublishScorePanel
                content={firstSelectedPost.content || ''}
                platform={firstSelectedPost.platform || 'facebook'}
                clientId={firstSelectedPost.client_id || undefined}
                contentType={firstSelectedPost.content_type || 'image'}
                scheduledTimeISO={
                  scheduledTime?.toISOString?.() ||
                  new Date(scheduledTime).toISOString()
                }
              />
            </Box>
          )}

          {selectedPostObjs.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Platforms before schedule
              </Typography>
              <FormHelperText sx={{ mx: 0, mb: 1 }}>
                Only connected platforms for each post&apos;s client. Gallery
                multi-promote already creates one draft per network — select
                those siblings. Adding extra networks here fans out into more
                scheduled posts (same content); first selected updates this
                draft&apos;s platform.
              </FormHelperText>
              {selectedPostObjs.map((post) => {
                const opts = connectedPlatformOptions(
                  platformAccounts,
                  post.client_id,
                );
                const selected = targetsByPostId[post.id] || [];
                return (
                  <Box
                    key={`tgt-${post.id}`}
                    sx={{
                      mb: 1.5,
                      p: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {post.title || `Post #${post.id}`}
                    </Typography>
                    {opts.length === 0 ? (
                      <Typography
                        variant="caption"
                        color="error"
                        display="block"
                      >
                        No connected platforms — connect on Clients.
                      </Typography>
                    ) : (
                      <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                        <InputLabel id={`bulk-plat-${post.id}`}>
                          Target platforms
                        </InputLabel>
                        <Select
                          labelId={`bulk-plat-${post.id}`}
                          label="Target platforms"
                          multiple
                          value={selected}
                          onChange={(e) => {
                            const next =
                              typeof e.target.value === 'string'
                                ? e.target.value.split(',')
                                : e.target.value;
                            setTargetsByPostId((prev) => ({
                              ...prev,
                              [post.id]: next,
                            }));
                          }}
                          renderValue={(vals) =>
                            vals
                              .map(
                                (v) =>
                                  opts.find((o) => o.value === v)?.label || v,
                              )
                              .join(', ')
                          }
                        >
                          {opts.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              <Checkbox
                                checked={selected.includes(opt.value)}
                              />
                              <ListItemText primary={opt.label} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Timezone (for sending schedule to API)</InputLabel>
            <Select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              label="Timezone (for sending schedule to API)"
            >
              <MenuItem value="America/New_York">
                Eastern (New York) — default for ML suggestions
              </MenuItem>
              <MenuItem value="UTC">UTC</MenuItem>
              <MenuItem value="America/Chicago">Central Time</MenuItem>
              <MenuItem value="America/Denver">Mountain Time</MenuItem>
              <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            fullWidth
            disabled={selectedPosts.length === 0 || savingTargets}
            onClick={handleSchedule}
          >
            Schedule {plannedScheduleCount} Post
            {plannedScheduleCount === 1 ? '' : 's'}
          </Button>
        </Grid>
      </Grid>

      <ConfirmDialog
        open={deleteConfirm.open}
        title={
          deleteConfirm.ids.length > 1
            ? `Delete ${deleteConfirm.ids.length} posts?`
            : 'Delete post?'
        }
        message={
          deleteConfirm.ids.length > 1
            ? `Remove ${deleteConfirm.ids.length} posts from the bulk scheduling queue? This permanently deletes them and cannot be undone.`
            : 'Remove this post from the bulk scheduling queue? This permanently deletes it and cannot be undone.'
        }
        confirmLabel="Delete"
        loading={deleteBusy}
        onCancel={() => {
          if (!deleteBusy) setDeleteConfirm({ open: false, ids: [] });
        }}
        onConfirm={handleConfirmDeleteFromBulk}
      />
    </Paper>
  );
};

// Recurring Schedule Component
const RecurringSchedule = ({ clients, onSchedule }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    content: '',
    platform: '',
    client_id: '',
    frequency: 'daily',
    start_date: new Date(),
    end_date: addDays(new Date(), 30),
    time_of_day: '09:00',
    days_of_week: [],
    day_of_month: 1,
  });

  const handleSubmit = async () => {
    try {
      const templatePost = {
        content: formData.content,
        platform: formData.platform,
        client_id: parseInt(formData.client_id),
      };

      const scheduleConfig = {
        frequency: formData.frequency,
        start_date: formData.start_date.toISOString(),
        end_date: formData.end_date.toISOString(),
        time_of_day: formData.time_of_day,
        days_of_week: formData.days_of_week,
        day_of_month: formData.day_of_month,
      };

      await onSchedule(templatePost, scheduleConfig);
      setOpen(false);
      setFormData({
        content: '',
        platform: '',
        client_id: '',
        frequency: 'daily',
        start_date: new Date(),
        end_date: addDays(new Date(), 30),
        time_of_day: '09:00',
        days_of_week: [],
        day_of_month: 1,
      });
    } catch (error) {
      console.error('Error creating recurring schedule:', error);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        startIcon={<Repeat />}
        onClick={() => setOpen(true)}
      >
        Create Recurring Schedule
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Recurring Schedule</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Post Content"
                multiline
                rows={4}
                fullWidth
                value={formData.content}
                onChange={(e) =>
                  setFormData({ ...formData, content: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Platform</InputLabel>
                <Select
                  value={formData.platform}
                  onChange={(e) =>
                    setFormData({ ...formData, platform: e.target.value })
                  }
                  label="Platform"
                >
                  <MenuItem value="facebook">Facebook</MenuItem>
                  <MenuItem value="twitter">Twitter</MenuItem>
                  {SHOW_INSTAGRAM_AS_PUBLISH_TARGET && (
                    <MenuItem value="instagram">Instagram</MenuItem>
                  )}
                  <MenuItem value="linkedin">LinkedIn</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Client</InputLabel>
                <Select
                  value={formData.client_id}
                  onChange={(e) =>
                    setFormData({ ...formData, client_id: e.target.value })
                  }
                  label="Client"
                >
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={formData.frequency}
                  onChange={(e) =>
                    setFormData({ ...formData, frequency: e.target.value })
                  }
                  label="Frequency"
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Time of Day"
                type="time"
                fullWidth
                value={formData.time_of_day}
                onChange={(e) =>
                  setFormData({ ...formData, time_of_day: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {formData.frequency === 'weekly' && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Days of Week
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(
                    (day, index) => (
                      <Chip
                        key={day}
                        label={day}
                        clickable
                        color={
                          formData.days_of_week.includes(index)
                            ? 'primary'
                            : 'default'
                        }
                        onClick={() => {
                          const days = formData.days_of_week.includes(index)
                            ? formData.days_of_week.filter((d) => d !== index)
                            : [...formData.days_of_week, index];
                          setFormData({ ...formData, days_of_week: days });
                        }}
                      />
                    )
                  )}
                </Box>
              </Grid>
            )}

            {formData.frequency === 'monthly' && (
              <Grid item xs={12} md={6}>
                <TextField
                  label="Day of Month"
                  type="number"
                  fullWidth
                  value={formData.day_of_month}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      day_of_month: parseInt(e.target.value),
                    })
                  }
                  inputProps={{ min: 1, max: 31 }}
                />
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Start Date"
                  value={formData.start_date}
                  onChange={(date) =>
                    setFormData({ ...formData, start_date: date })
                  }
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="End Date"
                  value={formData.end_date}
                  onChange={(date) =>
                    setFormData({ ...formData, end_date: date })
                  }
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Create Schedule
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Upcoming Posts Component
const UpcomingPosts = ({ posts, onReschedule, onPause, onPublishNow }) => {
  const [publishingId, setPublishingId] = useState(null);

  const handlePublishNow = async (post) => {
    if (!onPublishNow || publishingId) return;
    setPublishingId(post.id);
    try {
      await onPublishNow(post);
    } finally {
      setPublishingId(null);
    }
  };

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2 }, maxWidth: '100%', overflow: 'hidden' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Upcoming Posts
      </Typography>

      <Box sx={{ maxHeight: { xs: '70vh', md: 'none' }, overflowY: 'auto' }}>
        {posts.map((post) => (
          <Card key={post.id} sx={{ mb: 2, maxWidth: '100%' }}>
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'flex-start' },
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 1.5,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" sx={{ wordBreak: 'break-word' }}>
                    {post.title || 'Untitled'}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, wordBreak: 'break-word' }}
                  >
                    {post.content}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    {post.platform && (
                      <Chip label={post.platform} size="small" />
                    )}
                    {(post.client_name || post.client_id) && (
                      <Chip
                        label={post.client_name || `Client #${post.client_id}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: 'block', fontWeight: 600 }}
                  >
                    Due: {formatSchedulingTime(post.scheduled_time)}
                    {post.days_until > 0 &&
                      ` (${post.days_until}d ${post.hours_until}h)`}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    flexShrink: 0,
                    flexWrap: 'wrap',
                    flexDirection: { xs: 'column', sm: 'row' },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<Publish />}
                    disabled={publishingId === post.id}
                    onClick={() => handlePublishNow(post)}
                    sx={{
                      minHeight: 44,
                      textTransform: 'none',
                      width: { xs: '100%', sm: 'auto' },
                    }}
                  >
                    {publishingId === post.id ? 'Publishing…' : 'Publish now'}
                  </Button>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Reschedule">
                      <IconButton
                        onClick={() => onReschedule(post)}
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Pause">
                      <IconButton
                        onClick={() => onPause([post.id])}
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <Pause />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Paper>
  );
};

// AI Scheduling Recommendations Component
const AISchedulingRecommendations = ({ clients, onSchedule }) => {
  const { showError, showWarning } = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [formData, setFormData] = useState({
    client_id: '',
    platform: 'facebook',
    content_plan: [
      {
        content: '',
        title: '',
        hashtags: [],
        content_type: 'text',
        scheduled_day: 0,
        estimated_engagement: 0,
      },
    ],
  });

  const handleGetRecommendations = async () => {
    if (!formData.client_id) {
      showWarning('Please select a client');
      return;
    }

    setLoading(true);
    try {
      const response = await schedulingAPI.getAISchedulingRecommendations(
        parseInt(formData.client_id),
        formData.content_plan,
        formData.platform
      );
      setRecommendations(response);
    } catch (error) {
      console.error('Error getting AI recommendations:', error);
      showError('Failed to get AI recommendations');
    } finally {
      setLoading(false);
    }
  };

  const handleOptimizeSchedule = async () => {
    if (!formData.client_id) {
      showWarning('Please select a client');
      return;
    }

    setLoading(true);
    try {
      const response = await schedulingAPI.optimizeExistingSchedule(
        parseInt(formData.client_id)
      );
      setRecommendations(response);
    } catch (error) {
      console.error('Error optimizing schedule:', error);
      showError('Failed to optimize schedule');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="secondary"
        startIcon={<SmartToy />}
        onClick={() => setOpen(true)}
      >
        AI Scheduling Recommendations
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SmartToy />
            AI Scheduling Recommendations
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Client Selection */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Client</InputLabel>
                <Select
                  value={formData.client_id}
                  onChange={(e) =>
                    setFormData({ ...formData, client_id: e.target.value })
                  }
                  label="Client"
                >
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Platform</InputLabel>
                <Select
                  value={formData.platform}
                  onChange={(e) =>
                    setFormData({ ...formData, platform: e.target.value })
                  }
                  label="Platform"
                >
                  <MenuItem value="facebook">Facebook</MenuItem>
                  <MenuItem value="twitter">Twitter</MenuItem>
                  {SHOW_INSTAGRAM_AS_PUBLISH_TARGET && (
                    <MenuItem value="instagram">Instagram</MenuItem>
                  )}
                  <MenuItem value="linkedin">LinkedIn</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<Lightbulb />}
                  onClick={handleGetRecommendations}
                  disabled={loading}
                >
                  Get AI Recommendations
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<TrendingUp />}
                  onClick={handleOptimizeSchedule}
                  disabled={loading}
                >
                  Optimize Existing Schedule
                </Button>
              </Box>
            </Grid>

            {/* Recommendations Display */}
            {loading && (
              <Grid item xs={12}>
                <Alert severity="info">Getting AI recommendations...</Alert>
              </Grid>
            )}

            {recommendations && (
              <>
                {recommendations.source && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {AI_REC_SOURCE_LABELS[recommendations.source] ||
                          recommendations.source}
                      </Typography>
                      {recommendations.source_detail && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          {recommendations.source_detail}
                        </Typography>
                      )}
                    </Alert>
                  </Grid>
                )}
                {/* Best Posting Times */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography
                        variant="h6"
                        sx={{
                          mb: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Schedule />
                        Best Posting Times
                        <MlEstimateBadge basis="best_time_heuristic" />
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {recommendations.best_posting_times?.map(
                          (time, index) => (
                            <Chip
                              key={index}
                              label={time}
                              color="primary"
                              variant="outlined"
                            />
                          )
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Estimated Engagement */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography
                        variant="h6"
                        sx={{
                          mb: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <TrendingUp />
                        Estimated Engagement
                        <MlEstimateBadge basis="rule_based" />
                      </Typography>
                      <Box>
                        <Typography variant="body2">
                          Expected Rate:{' '}
                          {
                            recommendations.estimated_engagement
                              ?.expected_engagement_rate
                          }
                        </Typography>
                        <Typography variant="body2">
                          Optimal Frequency:{' '}
                          {
                            recommendations.estimated_engagement
                              ?.optimal_frequency
                          }
                        </Typography>
                        <Typography variant="body2">
                          Best Days:{' '}
                          {recommendations.estimated_engagement?.best_days?.join(
                            ', '
                          )}
                        </Typography>
                        <Typography variant="body2">
                          Confidence:{' '}
                          {
                            recommendations.estimated_engagement
                              ?.confidence_score
                          }
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Optimization Recommendations */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography
                        variant="h6"
                        sx={{
                          mb: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Lightbulb />
                        Optimization Recommendations
                      </Typography>
                      {recommendations.recommendations?.map((rec, index) => (
                        <Alert key={index} severity="info" sx={{ mb: 1 }}>
                          <Typography variant="body2">
                            <strong>{rec.type}:</strong> {rec.recommendation}
                          </Typography>
                        </Alert>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Optimal Schedule */}
                {recommendations.optimal_schedule &&
                  recommendations.optimal_schedule.length > 0 && (
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Typography
                            variant="h6"
                            sx={{
                              mb: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <CalendarToday />
                            Optimal Schedule
                          </Typography>
                          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                            {recommendations.optimal_schedule.map(
                              (post, index) => (
                                <Paper
                                  key={index}
                                  sx={{ p: 2, mb: 1, bgcolor: '#f5f5f5' }}
                                >
                                  <Typography variant="subtitle2">
                                    {post.title || `Post ${index + 1}`}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {post.content}
                                  </Typography>
                                  <Typography variant="caption">
                                    Scheduled:{' '}
                                    {formatSchedulingTime(post.scheduled_time)}
                                  </Typography>
                                </Paper>
                              )
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Main Scheduling Component
const Scheduling = () => {
  const { showSuccess, showError } = useToast();
  const { clients } = useClients();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(0);
  const [posts, setPosts] = useState([]);
  const [platformAccounts, setPlatformAccounts] = useState([]);
  const [upcomingPosts, setUpcomingPosts] = useState([]);
  const [stats, setStats] = useState({});
  const [error, setError] = useState('');
  const [rescheduleDialog, setRescheduleDialog] = useState({
    open: false,
    post: null,
    newTime: new Date(),
  });
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    postId: null,
  });
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    const t = (searchParams.get('tab') || '').toLowerCase();
    if (t in SCHEDULING_TAB_KEYS) {
      setTabValue(SCHEDULING_TAB_KEYS[t]);
      return;
    }
    const n = parseInt(t, 10);
    if (!Number.isNaN(n) && n >= 0 && n <= 5) setTabValue(n);
  }, [searchParams]);

  const loadData = useCallback(async (options = {}) => {
    const silent = Boolean(options.silent);
    if (!silent) setPageLoading(true);
    try {
      const results = await Promise.allSettled([
        postsAPI.getPosts(),
        schedulingAPI.getUpcomingPosts(),
        schedulingAPI.getSchedulingStats(),
        platformAccountsAPI.getPlatformAccounts(),
      ]);

      const pick = (i) =>
        results[i].status === 'fulfilled' ? results[i].value : null;
      const postsData = pick(0);
      const upcomingData = pick(1);
      const statsData = pick(2);
      const accountsData = pick(3);

      const failed = results
        .map((r, i) => (r.status === 'rejected' ? i : -1))
        .filter((i) => i >= 0);
      if (failed.length) {
        console.error(
          'Scheduling load: some requests failed (indices):',
          failed,
          failed.map((i) => results[i].reason),
        );
      }

      setPosts(Array.isArray(postsData) ? postsData : []);
      setUpcomingPosts(Array.isArray(upcomingData) ? upcomingData : []);
      setStats(statsData && typeof statsData === 'object' ? statsData : {});
      setPlatformAccounts(Array.isArray(accountsData) ? accountsData : []);

      if (failed.length === results.length) {
        setError('Failed to load scheduling data');
      } else if (!silent) {
        setError('');
      }
    } catch (error) {
      if (!silent) setError('Failed to load scheduling data');
      console.error('Error loading data:', error);
    } finally {
      if (!silent) setPageLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Keep Published / Today / This week current while Celery publishes in background.
  usePublishStatusPolling({
    posts,
    refreshPosts: loadData,
    showError,
    intervalMs: 8000,
  });

  useEffect(() => {
    const intervalId = setInterval(() => {
      loadData({ silent: true });
    }, 30000);
    return () => clearInterval(intervalId);
  }, [loadData]);

  const handleBulkSchedule = async (postIds, scheduledTime, timezone) => {
    try {
      const estTimeZone = 'America/New_York';
      const estISOString = scheduledTime
        ? formatInTimeZone(scheduledTime, estTimeZone, "yyyy-MM-dd'T'HH:mm")
        : null;
      await schedulingAPI.bulkSchedulePosts(postIds, estISOString, timezone);
      showSuccess('Posts scheduled successfully!');
      await loadData();
    } catch (error) {
      showError(
        error?.response?.data?.detail ||
          error?.message ||
          'Failed to schedule posts',
      );
    }
  };

  const handleRecurringSchedule = async (templatePost, scheduleConfig) => {
    try {
      const updatedConfig = {
        ...scheduleConfig,
        start_date: scheduleConfig.start_date.toISOString(),
        end_date: scheduleConfig.end_date.toISOString(),
      };
      await schedulingAPI.createRecurringSchedule(templatePost, updatedConfig);
      showSuccess('Recurring schedule created successfully!');
      await loadData();
    } catch (error) {
      showError(
        error?.response?.data?.detail ||
          error?.message ||
          'Failed to create recurring schedule',
      );
    }
  };

  const handleReschedule = (post) => {
    setRescheduleDialog({
      open: true,
      post,
      newTime: parseApiUtcDate(post?.scheduled_time) || new Date(),
    });
  };

  const handleCloseRescheduleDialog = () => {
    setRescheduleDialog({
      open: false,
      post: null,
      newTime: new Date(),
    });
  };

  const handleSubmitReschedule = async () => {
    if (!rescheduleDialog.post?.id || !rescheduleDialog.newTime) return;
    try {
      await schedulingAPI.reschedulePost(
        rescheduleDialog.post.id,
        rescheduleDialog.newTime,
      );
      showSuccess('Post rescheduled successfully!');
      handleCloseRescheduleDialog();
      await loadData();
    } catch (error) {
      showError(
        error?.response?.data?.detail ||
          error.message ||
          'Failed to reschedule post',
      );
    }
  };

  const handlePause = async (postIds) => {
    try {
      await schedulingAPI.pauseSchedule(postIds);
      showSuccess('Posts paused successfully!');
      await loadData();
    } catch (error) {
      showError(
        error?.response?.data?.detail ||
          error?.message ||
          'Failed to pause posts',
      );
    }
  };

  const handlePublishNow = async (post) => {
    if (!post?.id) return;
    try {
      await postsAPI.publishPost(post.id);
      showSuccess('Publish started');
      await loadData();
    } catch (error) {
      showError(
        error?.response?.data?.detail ||
          error?.message ||
          'Failed to publish post',
      );
      throw error;
    }
  };

  const handleOpenPostFromCalendar = (post) => {
    if (post?.id) {
      navigate(`/posts?post_id=${encodeURIComponent(post.id)}`);
    } else {
      navigate('/posts');
    }
  };

  const handleDeletePostFromCalendar = (postId) => {
    if (!postId) return;
    setDeleteConfirm({ open: true, postId });
  };

  const handleConfirmDeleteFromCalendar = async () => {
    const postId = deleteConfirm.postId;
    if (!postId) return;
    setDeleteBusy(true);
    try {
      await postsAPI.deletePost(postId);
      showSuccess('Post deleted');
      setDeleteConfirm({ open: false, postId: null });
      await loadData();
    } catch (err) {
      showError(
        err?.response?.data?.detail ||
          err.message ||
          'Could not delete post',
      );
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <Container maxWidth="xl">
      <PageHeader
        title="Scheduling"
        subtitle={
          <>
            One place for calendar, bulk & recurring, upcoming queue, AI slots,
            and the legacy scheduled-posts table. For create/publish workflows,
            use{' '}
            <Link component={RouterLink} to="/posts" underline="hover">
              Posts
            </Link>
            .
          </>
        }
      />

      <PageErrorAlert message={error} onClose={() => setError('')} />

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Scheduled Posts"
            value={stats.total_scheduled || 0}
            icon={<Schedule />}
            accentColor="#58a6ff"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Published Posts"
            value={stats.total_published || 0}
            icon={<TrendingUp />}
            accentColor="#238636"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Published today"
            value={stats.today_scheduled || 0}
            icon={<CalendarToday />}
            accentColor="#d29922"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Published this week"
            value={stats.week_scheduled || 0}
            icon={<Refresh />}
            accentColor="#58a6ff"
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3, maxWidth: '100%', overflow: 'hidden' }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Calendar" icon={<CalendarToday />} iconPosition="start" />
          <Tab label="Bulk" icon={<Schedule />} iconPosition="start" />
          <Tab label="Recurring" icon={<Repeat />} iconPosition="start" />
          <Tab label="Upcoming" icon={<TrendingUp />} iconPosition="start" />
          <Tab label="AI" icon={<SmartToy />} iconPosition="start" />
          <Tab label="Queue" icon={<Schedule />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {pageLoading ? (
        <PageLoadingState minHeight={400} message="Loading scheduling data…" />
      ) : (
        <>
      {tabValue === 0 && (
        <PostCalendar
          posts={posts}
          onEdit={handleOpenPostFromCalendar}
          onDelete={handleDeletePostFromCalendar}
          onView={handleOpenPostFromCalendar}
          onPreview={() => {}}
          onReschedule={handleReschedule}
        />
      )}

      {tabValue === 1 && (
        <BulkScheduling
          posts={posts}
          onSchedule={handleBulkSchedule}
          platformAccounts={platformAccounts}
          onRefresh={loadData}
        />
      )}

      {tabValue === 2 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <RecurringSchedule
            clients={clients}
            onSchedule={handleRecurringSchedule}
          />
        </Box>
      )}

      {tabValue === 3 && (
        <UpcomingPosts
          posts={upcomingPosts}
          onReschedule={handleReschedule}
          onPause={handlePause}
          onPublishNow={handlePublishNow}
        />
      )}

      {tabValue === 4 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <AISchedulingRecommendations
            clients={clients}
            onSchedule={handleRecurringSchedule}
          />
        </Box>
      )}

      {tabValue === 5 && <ScheduledPostsQueue />}

        </>
      )}

      <Dialog
        open={rescheduleDialog.open}
        onClose={handleCloseRescheduleDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reschedule Post</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="New Scheduled Time"
              value={rescheduleDialog.newTime}
              onChange={(date) =>
                setRescheduleDialog((prev) => ({ ...prev, newTime: date }))
              }
              renderInput={(params) => (
                <TextField {...params} fullWidth margin="normal" />
              )}
              minDateTime={new Date()}
            />
          </LocalizationProvider>
          <Typography variant="caption" color="text.secondary">
            Times are shown and rescheduled in Eastern Time.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRescheduleDialog}>Cancel</Button>
          <Button
            onClick={handleSubmitReschedule}
            variant="contained"
            disabled={!rescheduleDialog.newTime}
          >
            Reschedule
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteConfirm.open}
        title="Delete post"
        message="Delete this post from the calendar? This cannot be undone."
        confirmLabel="Delete"
        loading={deleteBusy}
        onCancel={() => {
          if (!deleteBusy) setDeleteConfirm({ open: false, postId: null });
        }}
        onConfirm={handleConfirmDeleteFromCalendar}
      />
    </Container>
  );
};

export default Scheduling;
